import { useEffect, useMemo, useState } from 'react';
import { Activity, CircleHelp, Layers, Timer, TrendingUp, Trophy } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import type { Address } from 'viem';
import { useAccount, useReadContract, useReadContracts, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { ERC20_ABI, PREDICTION_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import { useChainlinkPrice } from '../web3/useChainlinkPrice';
import { CHAINLINK_FEEDS } from '../web3/priceFeeds';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

const QUICK_AMOUNTS = [10, 50, 100];

function fmt(value: bigint | undefined, digits = 2) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

// live countdown string; returns '만기' once elapsed
function countdown(expirySec: number, nowMs: number): string {
  const s = Math.floor(expirySec - nowMs / 1000);
  if (s <= 0) return '만기 도달';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}일 ${h}시간 ${m}분`;
  if (h > 0) return `${h}시간 ${m}분 ${sec}초`;
  return `${m}분 ${sec}초`;
}

export default function PredictionView() {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const prediction = deployment?.prediction;

  const { data: marketCount } = useReadContract({
    address: prediction?.address,
    abi: PREDICTION_ABI,
    functionName: 'marketCount',
    chainId: readChainId,
    query: { enabled: Boolean(prediction) },
  });

  const marketContracts = useMemo(() => {
    if (marketCount === undefined || !prediction) return [];
    return Array.from({ length: Number(marketCount) }, (_, i) => ({
      address: prediction.address,
      abi: PREDICTION_ABI,
      functionName: 'markets',
      args: [BigInt(i)],
      chainId: readChainId,
    }) as const);
  }, [marketCount, prediction, readChainId]);

  const { data: allMarkets } = useReadContracts({
    contracts: marketContracts,
    query: { enabled: marketContracts.length > 0, refetchInterval: 15000 },
  });

  const stats = useMemo(() => {
    let volume = 0;
    let active = 0;
    let resolved = 0;
    const nowSec = Date.now() / 1000;
    for (const m of allMarkets ?? []) {
      if (m.status !== 'success') continue;
      const r = m.result as readonly [string, string, bigint, bigint, bigint, bigint, boolean, boolean];
      volume += Number(formatUnits(r[4], 18)) + Number(formatUnits(r[5], 18));
      if (r[6]) resolved += 1;
      else if (nowSec < Number(r[3])) active += 1;
    }
    return { total: allMarkets?.length ?? 0, active, resolved, volume };
  }, [allMarkets]);

  if (!prediction) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Prediction Market is not deployed on this network.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <CircleHelp size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">Prediction Market</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            코인 가격 예측에 베팅 — 만기 후 Chainlink 오라클로 자동 정산 (패리뮤추얼)
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Layers} label="전체 시장" value={String(stats.total)} />
        <StatCard icon={Activity} label="진행 중" value={String(stats.active)} accent />
        <StatCard
          icon={TrendingUp}
          label="총 베팅액"
          value={`${stats.volume.toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT`}
        />
        <StatCard icon={Trophy} label="정산 완료" value={String(stats.resolved)} />
      </div>

      {marketCount === undefined ? (
        <div className="text-xs text-foam-500 py-8 text-center">시장 정보를 불러오는 중...</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: Number(marketCount) }, (_, i) => (
            <MarketCard key={i} marketId={i} predictionAddress={prediction.address} />
          ))}
        </div>
      )}

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-850 text-[11px] text-foam-500 leading-relaxed">
        패리뮤추얼 방식: 이긴 쪽이 원금을 돌려받고 진 쪽 풀(수수료 1% 차감 후)을 지분대로 나눠 갖습니다.
        정산은 만기 후 누구나 실행할 수 있으며 Chainlink 온체인 가격만 사용합니다. 한쪽에만 베팅이
        몰리면 전원 원금 환급됩니다. (BSC 테스트넷 데모 — 실서비스 전 관할 규제 검토 필요)
      </div>
      <TrustBar contract={prediction.address} />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="p-4 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className={`text-lg font-semibold mt-1.5 tabular-nums truncate ${accent ? 'text-tide-400' : 'text-foam-100'}`}>
        {value}
      </div>
    </div>
  );
}

function MarketCard({ marketId, predictionAddress }: { marketId: number; predictionAddress: Address }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const usdtAddress = deployment?.tokens.USDT;

  const [amount, setAmount] = useState('');
  const [side, setSide] = useState<'yes' | 'no'>('yes');
  const [txError, setTxError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const amountWei = safeParse(amount);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { data: market, refetch: refetchMarket } = useReadContract({
    address: predictionAddress,
    abi: PREDICTION_ABI,
    functionName: 'markets',
    args: [BigInt(marketId)],
    chainId: readChainId,
    query: { refetchInterval: 15000 },
  });

  const question = market?.[0];
  const feed = market?.[1];
  const targetPrice = market?.[2];
  const expiry = market?.[3];
  const yesPool = market?.[4];
  const noPool = market?.[5];
  const resolved = market?.[6];
  const outcomeYes = market?.[7];

  // live oracle price for context, matched back to a symbol via the feed map
  const feedSymbol = feed
    ? Object.entries(CHAINLINK_FEEDS[97] ?? {}).find(([, a]) => a.toLowerCase() === feed.toLowerCase())?.[0]
    : undefined;
  const livePrice = useChainlinkPrice(feedSymbol ?? '');

  const { data: myYes, refetch: refetchYes } = useReadContract({
    address: predictionAddress,
    abi: PREDICTION_ABI,
    functionName: 'yesBets',
    args: address ? [BigInt(marketId), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address) },
  });

  const { data: myNo, refetch: refetchNo } = useReadContract({
    address: predictionAddress,
    abi: PREDICTION_ABI,
    functionName: 'noBets',
    args: address ? [BigInt(marketId), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address) },
  });

  const { data: myPayout, refetch: refetchPayout } = useReadContract({
    address: predictionAddress,
    abi: PREDICTION_ABI,
    functionName: 'payoutOf',
    args: address ? [BigInt(marketId), address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(address), refetchInterval: 15000 },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: usdtAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, predictionAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(usdtAddress && address) },
  });

  const needsApproval = amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed, error: receiptError, refetch: retryReceipt } = useWaitForTransactionReceipt({ hash, timeout: 300_000 });
  const [lastAction, setLastAction] = useState<'bet' | 'resolve' | 'claim'>('bet');

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isConfirmed && hash && address) {
      if (lastAction !== 'resolve') {
        logTransaction({
          type: lastAction === 'bet' ? 'predictionBet' : 'predictionClaim',
          hash,
          account: address,
          chainId: readChainId,
          details: { pool: `PREDICT-${marketId}`, amount: lastAction === 'bet' ? amount : '' },
        });
      }
      setAmount('');
      refetchMarket();
      refetchYes();
      refetchNo();
      refetchPayout();
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmed]);

  const isExpired = expiry !== undefined && nowMs / 1000 >= Number(expiry);

  const yesNum = yesPool !== undefined ? Number(formatUnits(yesPool, 18)) : 0;
  const noNum = noPool !== undefined ? Number(formatUnits(noPool, 18)) : 0;
  const total = yesNum + noNum;
  const yesPct = total > 0 ? (yesNum / total) * 100 : 50;

  // payout multiple if your side wins, before the 1% fee nuance
  const yesMultiple = yesNum > 0 ? 1 + (noNum * 0.99) / yesNum : undefined;
  const noMultiple = noNum > 0 ? 1 + (yesNum * 0.99) / noNum : undefined;

  // live distance between current oracle price and the target, and which side
  // the market would resolve to right now
  const targetUsd = targetPrice !== undefined ? Number(targetPrice) / 1e8 : undefined;
  const distancePct =
    livePrice !== undefined && targetUsd !== undefined && targetUsd > 0
      ? ((livePrice - targetUsd) / targetUsd) * 100
      : undefined;
  const currentlyYes = livePrice !== undefined && targetUsd !== undefined ? livePrice >= targetUsd : undefined;

  // projected payout if the chosen side wins, given the amount being entered
  const betAmt = amountWei !== null ? Number(amount) : 0;
  const projectedPayout =
    betAmt > 0
      ? side === 'yes'
        ? betAmt + (betAmt / (yesNum + betAmt)) * (noNum * 0.99)
        : betAmt + (betAmt / (noNum + betAmt)) * (yesNum * 0.99)
      : undefined;

  const handleApprove = () => {
    if (!usdtAddress) return;
    setTxError(null);
    writeApprove(
      { address: usdtAddress, abi: ERC20_ABI, functionName: 'approve', args: [predictionAddress, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleBet = () => {
    if (!amountWei) return;
    setTxError(null);
    setLastAction('bet');
    writeContract(
      {
        address: predictionAddress,
        abi: PREDICTION_ABI,
        functionName: 'bet',
        args: [BigInt(marketId), side === 'yes', amountWei],
      },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleResolve = () => {
    setTxError(null);
    setLastAction('resolve');
    writeContract(
      { address: predictionAddress, abi: PREDICTION_ABI, functionName: 'resolve', args: [BigInt(marketId)] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleClaim = () => {
    setTxError(null);
    setLastAction('claim');
    writeContract(
      { address: predictionAddress, abi: PREDICTION_ABI, functionName: 'claim', args: [BigInt(marketId)] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const busy = isApprovePending || isApproveConfirming || isPending || isConfirming;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="text-sm font-semibold text-foam-100 leading-snug">{question ?? '...'}</div>
        {resolved ? (
          <span
            className={`shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md ${
              outcomeYes ? 'text-tide-400 bg-tide-400/10' : 'text-coral-400 bg-coral-500/10'
            }`}
          >
            {outcomeYes ? 'YES 승' : 'NO 승'}
          </span>
        ) : isExpired ? (
          <span className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md text-amber-400 bg-amber-400/10">
            정산 대기
          </span>
        ) : (
          <span className="shrink-0 text-[11px] font-semibold px-2 py-1 rounded-md text-tide-400 bg-tide-400/10">
            진행 중
          </span>
        )}
      </div>

      {!resolved && currentlyYes !== undefined && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <span className="text-[11px] text-foam-500 flex items-center gap-1.5">
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${currentlyYes ? 'bg-tide-400' : 'bg-coral-400'}`} />
            현재 오라클 기준 판정
          </span>
          <span className={`text-xs font-semibold ${currentlyYes ? 'text-tide-400' : 'text-coral-400'}`}>
            {currentlyYes ? 'YES 우세' : 'NO 우세'}
            {distancePct !== undefined && (
              <span className="text-foam-500 font-normal ml-1.5">
                (기준가 대비 {distancePct >= 0 ? '+' : ''}
                {distancePct.toFixed(2)}%)
              </span>
            )}
          </span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
        <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <div className="text-foam-500">현재 시세</div>
          <div className="text-foam-100 font-medium mt-0.5 tabular-nums">
            {livePrice !== undefined ? `$${livePrice.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '-'}
          </div>
        </div>
        <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <div className="text-foam-500">기준가</div>
          <div className="text-foam-100 font-medium mt-0.5 tabular-nums">
            {targetUsd !== undefined ? `$${targetUsd.toLocaleString('en-US')}` : '-'}
          </div>
        </div>
        <div className="p-2 rounded-lg bg-abyss-900/60 border border-abyss-700">
          <div className="text-foam-500 flex items-center justify-center gap-1">
            <Timer size={10} />
            {isExpired || resolved ? '만기' : '남은 시간'}
          </div>
          <div className={`font-medium mt-0.5 tabular-nums ${isExpired && !resolved ? 'text-amber-400' : 'text-foam-100'}`}>
            {resolved
              ? '정산됨'
              : expiry !== undefined
                ? countdown(Number(expiry), nowMs)
                : '-'}
          </div>
        </div>
      </div>

      <div>
        <div className="flex justify-between items-end text-[11px] mb-1">
          <span className="text-tide-400 font-semibold tabular-nums">
            YES {yesPct.toFixed(0)}%
            <span className="text-foam-500 font-normal ml-1">
              {fmt(yesPool)} USDT{yesMultiple !== undefined ? ` ×${yesMultiple.toFixed(2)}` : ''}
            </span>
          </span>
          <span className="text-coral-400 font-semibold tabular-nums">
            <span className="text-foam-500 font-normal mr-1">
              {fmt(noPool)} USDT{noMultiple !== undefined ? ` ×${noMultiple.toFixed(2)}` : ''}
            </span>
            {(100 - yesPct).toFixed(0)}% NO
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-coral-500/40 overflow-hidden flex">
          <div className="h-full bg-tide-500 transition-all duration-500" style={{ width: `${yesPct}%` }} />
        </div>
      </div>

      {(myYes !== undefined && myYes > 0n) || (myNo !== undefined && myNo > 0n) ? (
        <div className="text-[11px] text-foam-500">
          내 베팅: {myYes !== undefined && myYes > 0n && <span className="text-tide-400">YES {fmt(myYes)} USDT</span>}
          {myYes !== undefined && myYes > 0n && myNo !== undefined && myNo > 0n && ' · '}
          {myNo !== undefined && myNo > 0n && <span className="text-coral-400">NO {fmt(myNo)} USDT</span>}
        </div>
      ) : null}

      {!resolved && !isExpired && (
        <>
          <div className="grid grid-cols-2 gap-1 p-0.5 rounded-lg bg-abyss-900 border border-abyss-700">
            {(['yes', 'no'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setSide(s)}
                className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${
                  side === s
                    ? s === 'yes'
                      ? 'bg-tide-500 text-abyss-950'
                      : 'bg-coral-500 text-abyss-950'
                    : 'text-foam-300 hover:text-foam-100'
                }`}
              >
                {s.toUpperCase()}
                {s === 'yes' && yesMultiple !== undefined ? ` ×${yesMultiple.toFixed(2)}` : ''}
                {s === 'no' && noMultiple !== undefined ? ` ×${noMultiple.toFixed(2)}` : ''}
              </button>
            ))}
          </div>

          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="USDT 수량"
            className="w-full px-3 py-2 rounded-lg border border-abyss-700 bg-abyss-900/60 text-sm text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
          />

          <div className="flex gap-1.5">
            {QUICK_AMOUNTS.map((q) => (
              <button
                key={q}
                onClick={() => setAmount(String(q))}
                className="flex-1 py-1 rounded-md text-[11px] font-medium bg-abyss-700 text-foam-300 hover:bg-abyss-600 transition-colors"
              >
                {q}
              </button>
            ))}
          </div>

          {projectedPayout !== undefined && (
            <div className="flex justify-between text-[11px] px-1">
              <span className="text-foam-500">{side.toUpperCase()} 적중 시 예상 수령</span>
              <span className="text-tide-400 font-semibold tabular-nums">
                ≈ {projectedPayout.toLocaleString('en-US', { maximumFractionDigits: 2 })} USDT
                <span className="text-foam-500 font-normal ml-1">
                  (×{(projectedPayout / betAmt).toFixed(2)})
                </span>
              </span>
            </div>
          )}

          <button
            onClick={needsApproval ? handleApprove : handleBet}
            disabled={!isConnected || !onCorrectChain || !amountWei || busy}
            className={`w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed ${
              side === 'no' && !needsApproval
                ? 'bg-coral-500 text-abyss-950 hover:bg-coral-400'
                : 'bg-tide-500 text-abyss-950 hover:bg-tide-400'
            }`}
          >
            {!isConnected
              ? '지갑을 연결하세요'
              : !onCorrectChain
                ? '네트워크를 전환하세요'
                : busy
                  ? '처리 중...'
                  : needsApproval && amountWei
                    ? 'Approve USDT'
                    : `${side.toUpperCase()}에 베팅`}
          </button>
        </>
      )}

      {!resolved && isExpired && (
        <button
          onClick={handleResolve}
          disabled={!isConnected || !onCorrectChain || busy}
          className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-amber-400 text-abyss-950 hover:bg-amber-300"
        >
          {busy ? '처리 중...' : '오라클로 정산 실행 (누구나 가능)'}
        </button>
      )}

      {resolved && myPayout !== undefined && myPayout > 0n && (
        <button
          onClick={handleClaim}
          disabled={!isConnected || !onCorrectChain || busy}
          className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
        >
          {busy ? '처리 중...' : `수령하기 (${fmt(myPayout, 4)} USDT)`}
        </button>
      )}

      {txError && (
        <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: receiptError, hash, retry: retryReceipt },
      ]} />
    </div>
  );
}
